import type { ErrorState } from '../../reducers/types/reducerTypes'
import type { ErrorStatus } from './types'
import { ERROR_STATUS } from './types'

interface ErrorConfig {
  errorTitle: string
  errorText: string
  goBackButtonText: string
  shouldDisplayTryAgainButton: boolean
  shouldDisplayGoBackButton: boolean
  shouldDisplayExitButton: boolean
}

interface ErrorStrategy {
  getConfig(error?: ErrorState): ErrorConfig
}

class FatalErrorStrategy implements ErrorStrategy {
  getConfig(): ErrorConfig {
    return {
      errorTitle: 'The PBS app encountered an error and needs to be restarted',
      errorText:
        'An error report has been submitted. We are sorry for the inconvenience. Visit help.pbs.org if the problem persists. Error: Fatal error',
      goBackButtonText: 'Restart App',
      shouldDisplayTryAgainButton: false,
      shouldDisplayGoBackButton: true,
      shouldDisplayExitButton: false,
    }
  }
}

class GeoErrorStrategy implements ErrorStrategy {
  getConfig(error: ErrorState): ErrorConfig {
    return {
      errorTitle: 'Video not available',
      errorText: `This video is not available in your region (Error: ${error.code})`,
      goBackButtonText: 'Ok',
      shouldDisplayTryAgainButton: false,
      shouldDisplayGoBackButton: true,
      shouldDisplayExitButton: false,
    }
  }
}

class DRMErrorStrategy implements ErrorStrategy {
  getConfig(error: ErrorState): ErrorConfig {
    return {
      errorTitle: 'Unable to Play Video',
      errorText: `An error ocurred while decrypting DRM content (Error: ${error.code})`,
      goBackButtonText: 'Go Back',
      shouldDisplayTryAgainButton: true,
      shouldDisplayGoBackButton: true,
      shouldDisplayExitButton: false,
    }
  }
}

class VideoUnavailableErrorStrategy implements ErrorStrategy {
  getConfig(): ErrorConfig {
    return {
      errorTitle: 'Video not available',
      errorText: 'This video is currently not available for playback.',
      goBackButtonText: 'Go Back',
      shouldDisplayTryAgainButton: false,
      shouldDisplayGoBackButton: true,
      shouldDisplayExitButton: false,
    }
  }
}

class HTTP404ErrorStrategy implements ErrorStrategy {
  getConfig(): ErrorConfig {
    return {
      //TODO: will the ' fail?
      errorTitle: "We weren't able to find that content.",
      errorText: '',
      goBackButtonText: 'Go Back',
      shouldDisplayTryAgainButton: false,
      shouldDisplayGoBackButton: true,
      shouldDisplayExitButton: false,
    }
  }
}

class HTTP408ErrorStrategy implements ErrorStrategy {
  getConfig(): ErrorConfig {
    return {
      errorTitle: 'A request timed out. Please try again.',
      errorText: '',
      goBackButtonText: 'Go Back',
      shouldDisplayTryAgainButton: true,
      shouldDisplayGoBackButton: true,
      shouldDisplayExitButton: false,
    }
  }
}

class HTTP100ErrorStrategy implements ErrorStrategy {
  getConfig(error: ErrorState): ErrorConfig {
    return {
      errorTitle: 'Video Playback Error',
      errorText: `An unexpected error occurred with video playback (Error: ${error.code})`,
      goBackButtonText: 'Go Back',
      shouldDisplayTryAgainButton: false,
      shouldDisplayGoBackButton: true,
      shouldDisplayExitButton: false,
    }
  }
}

class NoSeeAllContentStrategy implements ErrorStrategy {
  getConfig(): ErrorConfig {
    return {
      errorTitle: 'Unable to load content.',
      errorText: '',
      goBackButtonText: 'Go Back',
      shouldDisplayTryAgainButton: false,
      shouldDisplayGoBackButton: true,
      shouldDisplayExitButton: false,
    }
  }
}
class LocalizationErrorStrategy implements ErrorStrategy {
  getConfig(): ErrorConfig {
    return {
      errorTitle: 'Unable to find your station.',
      errorText:
        "We're unable to find your local PBS station. If the Try Again button doesn't work, please sign in at pbs.org and select your station, then retry opening the app. Need help? Email us at help@pbs.org.",
      goBackButtonText: 'Exit App',
      shouldDisplayTryAgainButton: true,
      shouldDisplayGoBackButton: false,
      shouldDisplayExitButton: true,
    }
  }
}
class ChangeStationErrorStrategy implements ErrorStrategy {
  getConfig(): ErrorConfig {
    return {
      errorTitle: 'Unable to load station details.',
      errorText:
        'This station is currently unavailable. Please select another station to continue watching. Need help? Email us at help@pbs.org.',
      goBackButtonText: 'OK',
      shouldDisplayTryAgainButton: false,
      shouldDisplayGoBackButton: true,
      shouldDisplayExitButton: false,
    }
  }
}

class CommonError implements ErrorStrategy {
  getConfig(error: ErrorState): ErrorConfig {
    return {
      errorTitle: 'An unexpected error occurred.',
      errorText: 'Please try again, or exit and relaunch the app.',
      goBackButtonText: 'Go Back',
      shouldDisplayTryAgainButton: error.content.errorPath ? true : false,
      shouldDisplayGoBackButton: true,
      shouldDisplayExitButton: true,
    }
  }
}

export const errorStrategies: Record<ErrorStatus, ErrorStrategy> = {
  [ERROR_STATUS.FATAL_ERROR]: new FatalErrorStrategy(),
  [ERROR_STATUS.GEO_ERROR]: new GeoErrorStrategy(),
  [ERROR_STATUS.DRM_ERROR]: new DRMErrorStrategy(),
  [ERROR_STATUS.VIDEO_UNAVAILABLE_ERROR]: new VideoUnavailableErrorStrategy(),
  [ERROR_STATUS.HTTP404_ERROR]: new HTTP404ErrorStrategy(),
  [ERROR_STATUS.HTTP408_ERROR]: new HTTP408ErrorStrategy(),
  [ERROR_STATUS.HTTP100_ERROR]: new HTTP100ErrorStrategy(),
  [ERROR_STATUS.NO_SEE_ALL_CONTENT]: new NoSeeAllContentStrategy(),
  [ERROR_STATUS.LOCALIZATION_ERROR]: new LocalizationErrorStrategy(),
  [ERROR_STATUS.UNAVAILABLE_STATION]: new ChangeStationErrorStrategy(),
  [ERROR_STATUS.DEFAULT]: new CommonError(),
}

export function getErrorStrategy(errorStatus: string): ErrorStrategy {
  return (
    errorStrategies[errorStatus as ErrorStatus] ||
    errorStrategies[ERROR_STATUS.DEFAULT]
  )
}

export function getErrorConfig(error: ErrorState): ErrorConfig {
  const errorStatus = error.status || ERROR_STATUS.DEFAULT
  const strategy = getErrorStrategy(errorStatus)
  return strategy.getConfig(error)
}
